import { Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { ReactComponent as AlertCircleIcon } from "assets/icons/alert-circle.svg";
import { ReactComponent as AlertTriangleIcon } from "assets/icons/alert-triangle.svg";
import { ReactComponent as CheckCircleIcon } from "assets/icons/check-circle.svg";
import ERoutes from "./types/Routes";

import ProtectedRoutes from "./components/ProtectedRoutes";
import UserManagementLayout from "./layouts/UserManagement";
import DynamicUI from "./pages/DynamicUI";
import ForgotPassword from "./pages/ForgotPassword";
import Home from "./pages/Home";
import LoginPage from "./pages/Login";
import Marketing from "./pages/Marketing";
import Merchandising from "./pages/Merchandising";
import NotFound from "pages/NotFound";
import RegisterUser from "./pages/SignUp";

import "react-toastify/dist/ReactToastify.min.css";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "api/api";
import { selectUserData, verifyToken } from "store/auth/authSlice";
import { VDPResultsV2 } from "pages/v2/VDPResults";
import Reporting from "pages/v2/Reporting";
import LeadDetails from "pages/v2/Reporting/components/LeadDetails";
import VDPBoostReporting from "pages/v2/Reporting/components/VDPBoost";
import ResultScoreDetails from "pages/v2/Reporting/components/ResultScoreDetails";
import MoneyList from "pages/v2/Reporting/MoneyList";
import ChangePassword from "pages/ChangePassword";
import { ImportFilePage } from "pages/v2/ImportFile/ImportFilePage";
// import ResultScoreDetails from "pages/v3/Reporting/ResultScoreDetails";

const App = () => {

  const location = useLocation();

  const dispatch = useAppDispatch();

  const userAuth = useAppSelector(selectUserData);

  const queryClient = useQueryClient();

  const hadSession = useRef(Boolean(userAuth.accessToken));

  // When the session ends without a page reload (expired token / 401),
  // drop cached API data so the next user doesn't see the previous user's data.
  useEffect(() => {
    if (userAuth.accessToken) {
      hadSession.current = true;
      return;
    }

    if (hadSession.current) {
      hadSession.current = false;
      dispatch(api.util.resetApiState());
      queryClient.clear();
    }
  }, [userAuth.accessToken, dispatch, queryClient]);

  useEffect(() => {
    // Function to dispatch verifyToken
    const verifyUserToken = () => {
      dispatch(verifyToken({
        email: userAuth.email || '',
        token: userAuth.accessToken || '',
      }));
    };

    // Call verifyUserToken immediately to verify token on component mount or path change
    verifyUserToken();

    // Set up interval to call verifyUserToken every 1 hour
    const intervalId = setInterval(verifyUserToken, 60 * 60 * 1000); // 3600000ms = 1 hour

    // Clean up interval on component unmount or when dependencies change
    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, dispatch, userAuth.email, userAuth.accessToken]);


  // TODO: ADD A LOADING SCREEN OR OVERLAY
  return (
    <>
      <ToastContainer
        theme="colored"
        icon={({ type }) => {
          switch (type) {
            case "default":
            case "info":
            case "error":
              return <AlertCircleIcon />;

            case "success":
              return <CheckCircleIcon />;

            case "warning":
              return <AlertTriangleIcon />;

            default:
              return null;
          }
        }}
      />
      {/* <Router> */}
        <Routes>
          <Route
            path={`${ERoutes.Home}*`}
            element={
              <ProtectedRoutes>
                <Routes>
                  <Route index element={<Home />} />
                  <Route path={ERoutes.ImportFile} element={<ImportFilePage />} />
                  <Route path={ERoutes.Reporting} element={<Reporting />} />
                  <Route path={ERoutes.LeadDetails} element={<LeadDetails />} />
                  <Route path={ERoutes.ResultScoreDetails} element={<ResultScoreDetails />} />
                  <Route path={ERoutes.VDPBoost} element={<VDPBoostReporting />} />
                  <Route path={ERoutes.MoneyList} element={
                        // <MoneyListGuardLayout>
                          <MoneyList />
                        // </MoneyListGuardLayout>
                      } />
                  <Route
                    path={ERoutes.Merchandising}
                    element={<Merchandising />}
                  />
                  <Route
                    path={ERoutes.MarketingResults}
                    element={<Marketing />}
                  />
                  <Route path={ERoutes.VDPResults} element={<VDPResultsV2 />} />

                  <Route path="/dynamic-ui" element={<DynamicUI />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </ProtectedRoutes>
            }
          />

          <Route element={<UserManagementLayout />}>
            <Route path={ERoutes.ForgotPassword} element={<ForgotPassword />} />

            <Route path={ERoutes.Login} element={<LoginPage />} />
            <Route path={ERoutes.SignUp} element={<RegisterUser />} />
            <Route path={ERoutes.ChangePassword} element={<ChangePassword />} />
          </Route>
        </Routes>
      {/* </Router> */}
    </>
  );
};

export default App;
